import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import {
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { ContractsIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { ExtractionsPageContent } from '@/app/components/features/evaluations/Extractions/ExtractionsPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSetAtom } from 'jotai';
import { createExtractionOpenAtom } from '@/app/components/features/evaluations/Extractions/store';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/extractions/',
)({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      id: z.string().optional(),
      extractionId: z.string().optional(),
      queryId: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.extractions');
  const { contractId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const summaryType = SummarizationNamespaces.CONTRACT;
  const setIsNewModalOpen = useSetAtom(createExtractionOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={contractId}
          documentType={DocumentTypes.CONTRACT}
          onClick={() => setIsNewModalOpen(true)}
          aria-label={tFeature('buttons.create')}
        >
          <HugeiconsIcon icon={PlusSignIcon} />
        </RunAnalysisHeaderButton>
      </Header>
      <ExtractionsPageContent
        summaryType={summaryType}
        evaluationId={contractId}
        evalLanguage={evaluation.language}
      />
    </>
  );
}
